import { Alert, Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import type {
    WasteMovementFormValues,
    WasteMovementHazardousComponentFormRow,
    WasteMovementPopComponentFormRow,
} from "../model/formValues";
import type { WasteMovementReferenceLookups } from "../model/referenceLookups";
import {
    NO_CONSIGNMENT_REASON_OPTIONS,
    SOURCE_NOT_PROVIDED,
    SOURCE_OF_COMPONENTS_OPTIONS,
    SOURCE_PROVIDED_WITH_WASTE,
    consignmentSectionState,
    hasHazardousFormDetail,
    hasPopsFormDetail,
    type ComponentRowErrors,
    type EffectiveEwcClassification,
    type HazardousPopsErrors,
    type StoredHazardousPopsState,
} from "../core/wasteMovementHazardousPops";

export type HazardousPopsSectionsProps = {
    values: WasteMovementFormValues;
    errors: HazardousPopsErrors;
    /** Same visibility rule as the rest of the form (persisted record, after a save attempt, or live validation). */
    showErrors: boolean;
    readOnlyForm: boolean;
    isNew: boolean;
    lookups: Pick<WasteMovementReferenceLookups, "hazardousPropertyCodes" | "popCodes">;
    ewc: EffectiveEwcClassification;
    stored: StoredHazardousPopsState;
    requiredMarker: JSX.Element | null;
    updateField: (field: keyof WasteMovementFormValues, value: string) => void;
    applyValues?: (updater: (current: WasteMovementFormValues) => WasteMovementFormValues) => void;
};

function ErrorText({ message, show }: { message?: string; show: boolean }) {
    if (!show || !message) return null;
    return <div className="invalid-feedback d-block">{message}</div>;
}

function listError(value: HazardousPopsErrors["hazardousComponents"]): string | undefined {
    return typeof value === "string" ? value : undefined;
}

function rowError(value: HazardousPopsErrors["hazardousComponents"], index: number): ComponentRowErrors | undefined {
    return Array.isArray(value) ? value[index] : undefined;
}

const hpLabel = (code: string) => code.replace("_", " ");

/**
 * S1C sections of the Waste Received form: hazardous detail, POP detail, hazardous waste consignment, and the explicit
 * confirmation needed before stored detail is removed after an answer changes from Yes to No.
 */
export function HazardousPopsSections(props: HazardousPopsSectionsProps) {
    const { values, stored, isNew } = props;
    const consignment = consignmentSectionState(values, props.ewc, stored);
    return (
        <>
            {values.containsHazardous !== "true" && !isNew && stored.hasHazardousDetail && (
                <RemovalConfirmation
                    {...props}
                    testId="hazardous-removal-confirmation"
                    description={describeHazardous(values)}
                    what="hazardous"
                    checked={values.confirmRemoveHazardousDetail}
                    field="confirmRemoveHazardousDetail"
                    error={props.errors.confirmRemoveHazardousDetail}
                />
            )}
            {values.containsHazardous === "true" && <HazardousDetailsSection {...props} />}
            {values.containsPops !== "true" && !isNew && stored.hasPopsDetail && (
                <RemovalConfirmation
                    {...props}
                    testId="pops-removal-confirmation"
                    description={describePops(values)}
                    what="POP"
                    checked={values.confirmRemovePopsDetail}
                    field="confirmRemovePopsDetail"
                    error={props.errors.confirmRemovePopsDetail}
                />
            )}
            {values.containsPops === "true" && <PopsDetailsSection {...props} />}
            {consignment.visible && <ConsignmentSection {...props} required={consignment.required} />}
        </>
    );
}

function describeHazardous(values: WasteMovementFormValues): string {
    const parts: string[] = [];
    const source = SOURCE_OF_COMPONENTS_OPTIONS.find((s) => s.code === values.hazardousSourceOfComponents);
    if (source) parts.push(`source: ${source.label.toLowerCase()}`);
    if (values.hazardousPropertyCodes.length) parts.push(`HP codes: ${values.hazardousPropertyCodes.map(hpLabel).join(", ")}`);
    if (values.hazardousComponents.length)
        parts.push(`${values.hazardousComponents.length} component${values.hazardousComponents.length === 1 ? "" : "s"}`);
    return parts.join("; ");
}

function describePops(values: WasteMovementFormValues): string {
    const parts: string[] = [];
    const source = SOURCE_OF_COMPONENTS_OPTIONS.find((s) => s.code === values.popsSourceOfComponents);
    if (source) parts.push(`source: ${source.label.toLowerCase()}`);
    if (values.popComponents.length) parts.push(`POPs: ${values.popComponents.map((p) => p.code).join(", ")}`);
    return parts.join("; ");
}

function RemovalConfirmation({
    testId,
    description,
    what,
    checked,
    field,
    error,
    showErrors,
    readOnlyForm,
    applyValues,
}: HazardousPopsSectionsProps & {
    testId: string;
    description: string;
    what: string;
    checked: boolean;
    field: "confirmRemoveHazardousDetail" | "confirmRemovePopsDetail";
    error?: string;
}) {
    return (
        <Alert variant="warning" className="py-2 mb-3" data-testid={testId}>
            <div className="mb-2">
                This movement has {what} details recorded{description ? ` (${description})` : ""}. You have answered No, so they
                will be removed when you save. To keep them, answer Yes.
            </div>
            <Form.Check
                type="checkbox"
                id={`waste-movement-${field}`}
                label={`Remove the recorded ${what} details`}
                checked={checked}
                disabled={readOnlyForm || !applyValues}
                onChange={(e) => applyValues?.((current) => ({ ...current, [field]: e.target.checked }))}
            />
            <ErrorText message={error} show={showErrors} />
        </Alert>
    );
}

function SourceSelect({
    id,
    label,
    field,
    value,
    error,
    showErrors,
    readOnlyForm,
    requiredMarker,
    updateField,
}: Pick<HazardousPopsSectionsProps, "showErrors" | "readOnlyForm" | "requiredMarker" | "updateField"> & {
    id: string;
    label: string;
    field: "hazardousSourceOfComponents" | "popsSourceOfComponents";
    value: string;
    error?: string;
}) {
    return (
        <Form.Group className="mb-3">
            <Form.Label htmlFor={id}>
                {label} {requiredMarker}
            </Form.Label>
            <Form.Select
                id={id}
                value={value}
                onChange={(e) => updateField(field, e.target.value)}
                className={`form-select${showErrors && error ? " is-invalid" : ""}`}
                disabled={readOnlyForm}
            >
                <option value="">Select…</option>
                {SOURCE_OF_COMPONENTS_OPTIONS.map((s) => (
                    <option key={s.code} value={s.code}>
                        {s.label}
                    </option>
                ))}
            </Form.Select>
            <ErrorText message={error} show={showErrors} />
        </Form.Group>
    );
}

function HazardousDetailsSection(props: HazardousPopsSectionsProps) {
    const { values, errors, showErrors, readOnlyForm, lookups, ewc, stored, isNew, requiredMarker, applyValues } = props;
    const entered = hasHazardousFormDetail(values);
    const outstanding = !isNew && stored.containsHazardous === true && !stored.hasHazardousDetail && !entered;
    const notProvided = values.hazardousSourceOfComponents === SOURCE_NOT_PROVIDED;
    const knownCodes = new Set(lookups.hazardousPropertyCodes.map((c) => c.code));
    const unlistedCodes = values.hazardousPropertyCodes.filter((c) => !knownCodes.has(c));
    const order = lookups.hazardousPropertyCodes.map((c) => c.code);

    const toggleCode = (code: string) =>
        applyValues?.((current) => {
            const selected = new Set(current.hazardousPropertyCodes);
            if (selected.has(code)) selected.delete(code);
            else selected.add(code);
            const sorted = [...selected].sort((a, b) => {
                const ia = order.indexOf(a);
                const ib = order.indexOf(b);
                return (ia < 0 ? Number.MAX_SAFE_INTEGER : ia) - (ib < 0 ? Number.MAX_SAFE_INTEGER : ib) || a.localeCompare(b);
            });
            return { ...current, hazardousPropertyCodes: sorted };
        });
    const setRow = (index: number, patch: Partial<WasteMovementHazardousComponentFormRow>) =>
        applyValues?.((current) => ({
            ...current,
            hazardousComponents: current.hazardousComponents.map((row, i) => (i === index ? { ...row, ...patch } : row)),
        }));
    const addRow = () =>
        applyValues?.((current) => ({ ...current, hazardousComponents: [...current.hazardousComponents, { name: "", concentration: "" }] }));
    const removeRow = (index: number) =>
        applyValues?.((current) => ({ ...current, hazardousComponents: current.hazardousComponents.filter((_, i) => i !== index) }));

    const missingConcentration = values.hazardousComponents.some((c) => c.concentration.trim() === "");

    return (
        <div className="waste-movement-form-panel mb-3" data-testid="hazardous-details-section">
            <div className="fw-semibold mb-2">Hazardous details</div>
            {ewc.isHazardous === false && (
                <Alert variant="danger" className="py-2 mb-3" data-testid="hazardous-ewc-non-hazardous">
                    EWC {ewc.code} is classified as non-hazardous. Hazardous properties can't be recorded against a non-hazardous
                    EWC code: check the EWC code, or answer No.
                </Alert>
            )}
            {ewc.isHazardous === null && !ewc.changed && (
                <Alert variant="info" className="py-2 mb-3" data-testid="hazardous-ewc-unknown">
                    This movement's EWC hazardous classification wasn't recorded when it was saved, so these details can't be
                    checked against it. It isn't assumed either way: because the waste is marked hazardous, a consignment code
                    or reason is needed.
                </Alert>
            )}
            {outstanding && (
                <Alert variant="warning" className="py-2 mb-3" data-testid="hazardous-details-outstanding">
                    Hazardous details haven't been recorded for this movement yet. They are needed before this receipt can be
                    reported; the rest of the record can still be saved without them.
                </Alert>
            )}
            <SourceSelect
                id="waste-movement-hazardous-source"
                label="How were the hazardous components determined?"
                field="hazardousSourceOfComponents"
                value={values.hazardousSourceOfComponents}
                error={errors.hazardousSourceOfComponents}
                {...props}
                requiredMarker={outstanding ? null : requiredMarker}
            />
            <Form.Group className="mb-3">
                <Form.Label id="waste-movement-hp-codes-label">
                    Hazardous property codes {outstanding ? null : requiredMarker}
                </Form.Label>
                <div className="waste-movement-hp-codes" role="group" aria-labelledby="waste-movement-hp-codes-label">
                    {lookups.hazardousPropertyCodes.map((hp) => (
                        <Form.Check
                            key={hp.code}
                            type="checkbox"
                            id={`waste-movement-hp-${hp.code}`}
                            label={`${hpLabel(hp.code)} – ${hp.description}`}
                            checked={values.hazardousPropertyCodes.includes(hp.code)}
                            disabled={readOnlyForm || !applyValues}
                            onChange={() => toggleCode(hp.code)}
                        />
                    ))}
                    {unlistedCodes.map((code) => (
                        <Form.Check
                            key={code}
                            type="checkbox"
                            id={`waste-movement-hp-${code}`}
                            label={`${hpLabel(code)} (not in the current list)`}
                            checked
                            disabled={readOnlyForm || !applyValues}
                            onChange={() => toggleCode(code)}
                        />
                    ))}
                </div>
                <ErrorText message={errors.hazardousPropertyCodes} show={showErrors} />
            </Form.Group>
            <Form.Group className="mb-2">
                <Form.Label>Chemical or biological components</Form.Label>
                {values.hazardousComponents.map((row, index) => {
                    const rowErrors = rowError(errors.hazardousComponents, index);
                    return (
                        <Row className="g-2 mb-2 align-items-start" key={index} data-testid="hazardous-component-row">
                            <Col md={7}>
                                <Form.Control
                                    aria-label={`Component ${index + 1} name`}
                                    value={row.name}
                                    maxLength={500}
                                    placeholder="e.g. Lead"
                                    onChange={(e) => setRow(index, { name: e.target.value })}
                                    className={`form-control${showErrors && rowErrors?.name ? " is-invalid" : ""}`}
                                    disabled={readOnlyForm}
                                />
                                <ErrorText message={rowErrors?.name} show={showErrors} />
                            </Col>
                            <Col md={4}>
                                <InputGroup>
                                    <Form.Control
                                        aria-label={`Component ${index + 1} concentration in mg/kg`}
                                        inputMode="decimal"
                                        value={row.concentration}
                                        placeholder="Concentration"
                                        onChange={(e) => setRow(index, { concentration: e.target.value })}
                                        className={`form-control${showErrors && rowErrors?.concentration ? " is-invalid" : ""}`}
                                        disabled={readOnlyForm}
                                    />
                                    <InputGroup.Text>mg/kg</InputGroup.Text>
                                </InputGroup>
                                <ErrorText message={rowErrors?.concentration} show={showErrors} />
                            </Col>
                            <Col md={1}>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    aria-label={`Remove component ${index + 1}`}
                                    onClick={() => removeRow(index)}
                                    disabled={readOnlyForm || !applyValues}
                                >
                                    Remove
                                </Button>
                            </Col>
                        </Row>
                    );
                })}
                <div>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={addRow}
                        disabled={readOnlyForm || !applyValues || notProvided}
                        data-testid="hazardous-add-component"
                    >
                        Add component
                    </Button>
                </div>
                {notProvided && values.hazardousComponents.length === 0 && (
                    <div className="form-text">No components are recorded when they were not provided.</div>
                )}
                <ErrorText message={listError(errors.hazardousComponents)} show={showErrors} />
                {values.hazardousSourceOfComponents === SOURCE_PROVIDED_WITH_WASTE && values.hazardousComponents.length === 0 && (
                    <div className="form-text" data-testid="hazardous-components-recommended">
                        DEFRA recommends recording the components provided with the waste. You can save without them.
                    </div>
                )}
                {!notProvided && missingConcentration && (
                    <div className="form-text" data-testid="hazardous-concentration-recommended">
                        DEFRA recommends a concentration for each component. Leave it blank if it isn't known.
                    </div>
                )}
            </Form.Group>
        </div>
    );
}

function PopsDetailsSection(props: HazardousPopsSectionsProps) {
    const { values, errors, showErrors, readOnlyForm, lookups, stored, isNew, requiredMarker, applyValues } = props;
    const entered = hasPopsFormDetail(values);
    const outstanding = !isNew && stored.containsPops === true && !stored.hasPopsDetail && !entered;
    const notProvided = values.popsSourceOfComponents === SOURCE_NOT_PROVIDED;
    const known = new Set(lookups.popCodes.map((p) => p.code));

    const setRow = (index: number, patch: Partial<WasteMovementPopComponentFormRow>) =>
        applyValues?.((current) => ({
            ...current,
            popComponents: current.popComponents.map((row, i) => (i === index ? { ...row, ...patch } : row)),
        }));
    const addRow = () => applyValues?.((current) => ({ ...current, popComponents: [...current.popComponents, { code: "", concentration: "" }] }));
    const removeRow = (index: number) =>
        applyValues?.((current) => ({ ...current, popComponents: current.popComponents.filter((_, i) => i !== index) }));

    const missingConcentration = values.popComponents.some((c) => c.concentration.trim() === "");

    return (
        <div className="waste-movement-form-panel mb-3" data-testid="pops-details-section">
            <div className="fw-semibold mb-2">Persistent organic pollutants (POPs)</div>
            {outstanding && (
                <Alert variant="warning" className="py-2 mb-3" data-testid="pops-details-outstanding">
                    POP details haven't been recorded for this movement yet. They are needed before this receipt can be reported;
                    the rest of the record can still be saved without them.
                </Alert>
            )}
            <SourceSelect
                id="waste-movement-pops-source"
                label="How were the POP components determined?"
                field="popsSourceOfComponents"
                value={values.popsSourceOfComponents}
                error={errors.popsSourceOfComponents}
                {...props}
                requiredMarker={outstanding ? null : requiredMarker}
            />
            <Form.Group className="mb-2">
                <Form.Label>POPs found</Form.Label>
                {values.popComponents.map((row, index) => {
                    const rowErrors = rowError(errors.popComponents, index);
                    return (
                        <Row className="g-2 mb-2 align-items-start" key={index} data-testid="pop-component-row">
                            <Col md={7}>
                                <Form.Select
                                    aria-label={`POP ${index + 1}`}
                                    value={row.code}
                                    onChange={(e) => setRow(index, { code: e.target.value })}
                                    className={`form-select${showErrors && rowErrors?.code ? " is-invalid" : ""}`}
                                    disabled={readOnlyForm}
                                >
                                    <option value="">Select POP…</option>
                                    {row.code && !known.has(row.code) && (
                                        <option value={row.code}>{row.code} (not in the current list)</option>
                                    )}
                                    {lookups.popCodes.map((pop) => (
                                        <option key={pop.code} value={pop.code}>
                                            {pop.chemicalName} ({pop.code})
                                        </option>
                                    ))}
                                </Form.Select>
                                <ErrorText message={rowErrors?.code} show={showErrors} />
                            </Col>
                            <Col md={4}>
                                <InputGroup>
                                    <Form.Control
                                        aria-label={`POP ${index + 1} concentration in mg/kg`}
                                        inputMode="decimal"
                                        value={row.concentration}
                                        placeholder="Concentration"
                                        onChange={(e) => setRow(index, { concentration: e.target.value })}
                                        className={`form-control${showErrors && rowErrors?.concentration ? " is-invalid" : ""}`}
                                        disabled={readOnlyForm}
                                    />
                                    <InputGroup.Text>mg/kg</InputGroup.Text>
                                </InputGroup>
                                <ErrorText message={rowErrors?.concentration} show={showErrors} />
                            </Col>
                            <Col md={1}>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    aria-label={`Remove POP ${index + 1}`}
                                    onClick={() => removeRow(index)}
                                    disabled={readOnlyForm || !applyValues}
                                >
                                    Remove
                                </Button>
                            </Col>
                        </Row>
                    );
                })}
                <div>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={addRow}
                        disabled={readOnlyForm || !applyValues || notProvided}
                        data-testid="pops-add-component"
                    >
                        Add POP
                    </Button>
                </div>
                {notProvided && values.popComponents.length === 0 && (
                    <div className="form-text">No POPs are recorded when the details were not provided.</div>
                )}
                <ErrorText message={listError(errors.popComponents)} show={showErrors} />
                {values.popsSourceOfComponents === SOURCE_PROVIDED_WITH_WASTE && values.popComponents.length === 0 && (
                    <div className="form-text" data-testid="pops-components-recommended">
                        DEFRA recommends recording the POPs provided with the waste. You can save without them.
                    </div>
                )}
                {!notProvided && missingConcentration && (
                    <div className="form-text" data-testid="pops-concentration-recommended">
                        DEFRA recommends a concentration for each POP. Leave it blank if it isn't known.
                    </div>
                )}
            </Form.Group>
        </div>
    );
}

function ConsignmentSection(props: HazardousPopsSectionsProps & { required: boolean }) {
    const { values, errors, showErrors, readOnlyForm, ewc, required, requiredMarker, updateField, applyValues } = props;
    const hasCode = values.hazardousWasteConsignmentCode.trim() !== "";
    return (
        <div className="waste-movement-form-panel mb-3" data-testid="consignment-section">
            <div className="fw-semibold mb-2">Hazardous waste consignment</div>
            {required && (
                <div className="form-text mb-2" data-testid="consignment-required-reason">
                    {ewc.isHazardous === true
                        ? `Needed because EWC ${ewc.code} is hazardous.`
                        : "Needed because the waste is marked hazardous and its EWC classification wasn't recorded."}
                </div>
            )}
            <Row className="mb-2">
                <Col md={6}>
                    <Form.Group>
                        <Form.Label htmlFor="waste-movement-consignment-code">
                            Consignment note code {required ? requiredMarker : null}
                        </Form.Label>
                        <Form.Control
                            id="waste-movement-consignment-code"
                            type="text"
                            maxLength={64}
                            value={values.hazardousWasteConsignmentCode}
                            placeholder="e.g. CJTILE/A0001"
                            onChange={(e) => {
                                const next = e.target.value;
                                if (next.trim() !== "" && values.reasonForNoConsignmentCode !== "" && applyValues) {
                                    applyValues((current) => ({
                                        ...current,
                                        hazardousWasteConsignmentCode: next,
                                        reasonForNoConsignmentCode: "",
                                    }));
                                    return;
                                }
                                updateField("hazardousWasteConsignmentCode", next);
                            }}
                            className={`form-control${showErrors && errors.hazardousWasteConsignmentCode ? " is-invalid" : ""}`}
                            disabled={readOnlyForm}
                        />
                        <ErrorText message={errors.hazardousWasteConsignmentCode} show={showErrors} />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group>
                        <Form.Label htmlFor="waste-movement-no-consignment-reason">
                            Reason there is no consignment code {required && !hasCode ? requiredMarker : null}
                        </Form.Label>
                        <Form.Select
                            id="waste-movement-no-consignment-reason"
                            value={values.reasonForNoConsignmentCode}
                            onChange={(e) => updateField("reasonForNoConsignmentCode", e.target.value)}
                            className={`form-select${showErrors && errors.reasonForNoConsignmentCode ? " is-invalid" : ""}`}
                            disabled={readOnlyForm || hasCode}
                        >
                            <option value="">{hasCode ? "Not applicable" : "Select…"}</option>
                            {NO_CONSIGNMENT_REASON_OPTIONS.map((r) => (
                                <option key={r.code} value={r.code}>
                                    {r.label}
                                </option>
                            ))}
                        </Form.Select>
                        <ErrorText message={errors.reasonForNoConsignmentCode} show={showErrors} />
                    </Form.Group>
                </Col>
            </Row>
        </div>
    );
}
